'use client';

import Image from 'next/image';
import Link from 'next/link';
import type { Ref } from 'react';
import { Icon } from './Icon';
import { useTrackInView } from '../hooks/useTrackInView';

export interface PromotionCardOffer {
  id: string;
  businessProfileId: string;
  companyName: string | null;
  vendorLogo: string | null;
  title: string;
  description: string | null;
  imageUrl: string | null;
}

export interface PromotionCardProps {
  promo: PromotionCardOffer;
  surface: 'homepage' | 'promotions_page';
  /** Caller decides the URL shape — e.g. "/vendors/42" or "/star3/vendors/42". */
  vendorHref: string;
  /** Fired once when the card hits >= 50% viewport. */
  onView: (toOfferId: string) => void;
  /** Fired on outer <Link> click. */
  onClick: (toOfferId: string) => void;
}

/**
 * Shared promotion card used on the homepage Promotions section and the
 * dedicated Promotions page. Markup is a verbatim port of the inline card
 * JSX from the original PromotionsSection.
 *
 * All colors and fonts flow through `--brand-*` CSS variables.
 *
 * View tracking: a one-shot IntersectionObserver fires `onView(promo.id)`
 * when the card first crosses 50% of the viewport.
 */
export function PromotionCard({
  promo,
  vendorHref,
  onView,
  onClick,
}: PromotionCardProps) {
  const ref = useTrackInView<HTMLAnchorElement>({
    threshold: 0.5,
    onView: () => onView(promo.id),
  });

  return (
    <Link
      ref={ref as unknown as Ref<HTMLAnchorElement>}
      href={vendorHref}
      onClick={() => onClick(promo.id)}
      className="luxury-card focus-luxury group block overflow-hidden rounded-xl border"
      style={{
        borderColor: 'var(--brand-card-border)',
        boxShadow: 'var(--shadow-resting)',
        backgroundColor: 'var(--brand-card-bg)',
      }}
    >
      {/* Promotion image */}
      <div
        className="relative overflow-hidden"
        style={{ aspectRatio: '16 / 10' }}
      >
        {promo.imageUrl ? (
          <Image
            src={promo.imageUrl}
            alt={promo.title}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            style={{ transitionTimingFunction: 'var(--ease-luxury)' }}
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center"
            style={{
              backgroundColor: 'var(--brand-surface-subtle)',
            }}
          >
            <span
              className="text-sm"
              style={{ color: 'var(--brand-input-placeholder)' }}
            >
              No image
            </span>
          </div>
        )}
      </div>

      {/* Promotion content */}
      <div className="p-5">
        {/* Vendor logo (line 1) + name (line 2) */}
        {promo.companyName && (
          <div className="mb-3 flex flex-col items-start gap-1.5">
            {promo.vendorLogo ? (
              <Image
                src={promo.vendorLogo}
                alt={promo.companyName}
                width={48}
                height={48}
                className="rounded-md object-contain"
                style={{ width: 'auto', height: '48px', maxWidth: '120px' }}
              />
            ) : (
              <div
                className="flex h-12 w-12 items-center justify-center rounded-md text-base font-semibold"
                style={{
                  backgroundColor: 'var(--brand-surface-subtle)',
                  color: 'var(--brand-input-placeholder)',
                }}
              >
                {promo.companyName.charAt(0)}
              </div>
            )}
            <span
              className="truncate text-xs font-medium"
              style={{
                color: 'var(--brand-input-placeholder)',
                letterSpacing: '0.01em',
              }}
            >
              {promo.companyName}
            </span>
          </div>
        )}

        <h4
          className="mb-1 truncate font-semibold"
          style={{
            fontFamily: 'var(--brand-font-heading)',
            color: 'var(--brand-text)',
            fontSize: '15px',
            letterSpacing: '-0.01em',
          }}
        >
          {promo.title}
        </h4>

        {promo.description && (
          <p
            className="mb-3 line-clamp-2 text-sm"
            style={{
              fontFamily: 'var(--brand-font-body)',
              color: 'var(--brand-text-muted)',
              lineHeight: '1.5',
            }}
          >
            {promo.description}
          </p>
        )}

        <span
          className="inline-flex items-center gap-1 text-sm font-medium transition-colors duration-150"
          style={{
            color: 'var(--brand-primary, #014F8A)',
            letterSpacing: '0.02em',
          }}
        >
          View Offer
          <Icon
            name="chevron_right"
            size={14}
            className="transition-transform duration-200 group-hover:translate-x-0.5"
          />
        </span>
      </div>
    </Link>
  );
}
